import React, { useEffect, useState } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  Typography,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Avatar,
  TableSortLabel,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import NotificationsIcon from "@mui/icons-material/Notifications";
import SecurityIcon from "@mui/icons-material/Security";
import { useQuery, useMutation } from "urql";
import { useNotification } from "../hooks/useNotification";
import { USERS_QUERY, UPDATE_USER_MUTATION } from "../graphql/mutations";

const validateImageUrl = (url) => {
  return new Promise((resolve) => {
    if (!url) {
      resolve(null);
      return;
    }

    const img = new Image();
    img.onload = () => {
      if (img.width >= 50 && img.height >= 50) {
        resolve(url);
      } else {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
};

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [sortedUsers, setSortedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newRole, setNewRole] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [validatedImages, setValidatedImages] = useState({});
  const [orderBy, setOrderBy] = useState(null);
  const [order, setOrder] = useState("asc");
  const { showNotification } = useNotification();

  const [usersResult, fetchUsers] = useQuery({
    query: USERS_QUERY,
  });

  const [, updateUser] = useMutation(UPDATE_USER_MUTATION);

  useEffect(() => {
    if (usersResult.data?.users) {
      setUsers(usersResult.data.users);
      setSortedUsers(usersResult.data.users);
      setLoading(false);

      // Validate all user profile images
      usersResult.data.users.forEach(async (user) => {
        if (user.photoURL) {
          const validUrl = await validateImageUrl(user.photoURL);
          setValidatedImages((prev) => ({
            ...prev,
            [user.id]: validUrl,
          }));
        }
      });
    }
  }, [usersResult.data]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);

    const sorted = [...users].sort((a, b) => {
      let aValue = a[property];
      let bValue = b[property];

      // Handle special cases
      if (property === "name") {
        aValue = [a.firstName, a.lastName].filter(Boolean).join(" ");
        bValue = [b.firstName, b.lastName].filter(Boolean).join(" ");
      }

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) aValue = "";
      if (bValue === null || bValue === undefined) bValue = "";

      // Convert to lowercase for string comparison
      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (isAsc) {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    setSortedUsers(sorted);
  };

  const handleRoleChange = (user, newRole) => {
    setSelectedUser(user);
    setNewRole(newRole);
    setOpenDialog(true);
  };

  const handleConfirmRoleChange = async () => {
    try {
      const result = await updateUser({
        input: {
          id: selectedUser.id,
          role: newRole,
        },
      });

      if (result.data?.updateUser) {
        const updatedUsers = users.map((user) =>
          user.id === selectedUser.id ? result.data.updateUser : user
        );
        setUsers(updatedUsers);
        setSortedUsers(updatedUsers);
        showNotification("User role updated successfully", {
          severity: "success",
          autoHideDuration: 3000,
        });
      } else if (result.error) {
        showNotification(result.error.message, {
          severity: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      showNotification("Error updating user role", {
        severity: "error",
        autoHideDuration: 3000,
      });
    }
    setOpenDialog(false);
  };

  const handleRowClick = (user) => {
    setSelectedUser(user);
    setOpenDetailsDialog(true);
  };

  const getRoleColor = (role) => {
    switch (role) {
      case "ADMIN":
        return "error";
      case "MODERATOR":
        return "warning";
      case "REGISTRANT":
        return "info";
      default:
        return "default";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        User Management
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Profile</TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "id"}
                  direction={orderBy === "id" ? order : "asc"}
                  onClick={() => handleRequestSort("id")}
                >
                  ID
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "username"}
                  direction={orderBy === "username" ? order : "asc"}
                  onClick={() => handleRequestSort("username")}
                >
                  Username
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "name"}
                  direction={orderBy === "name" ? order : "asc"}
                  onClick={() => handleRequestSort("name")}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "role"}
                  direction={orderBy === "role" ? order : "asc"}
                  onClick={() => handleRequestSort("role")}
                >
                  Role
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedUsers.map((user) => (
              <TableRow
                key={user.id}
                hover
                onClick={() => handleRowClick(user)}
                sx={{ cursor: "pointer" }}
              >
                <TableCell>
                  <Avatar
                    src={validatedImages[user.id] || null}
                    sx={{ width: 40, height: 40 }}
                  >
                    <PersonIcon />
                  </Avatar>
                </TableCell>
                <TableCell>{user.id}</TableCell>
                <TableCell>{user.username || "N/A"}</TableCell>
                <TableCell>
                  {[user.firstName, user.lastName].filter(Boolean).join(" ") ||
                    "N/A"}
                </TableCell>
                <TableCell>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                      value={user.role}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleRoleChange(user, e.target.value);
                      }}
                      label="Role"
                    >
                      <MenuItem value="USER">User</MenuItem>
                      <MenuItem value="ADMIN">Admin</MenuItem>
                      <MenuItem value="MODERATOR">Moderator</MenuItem>
                      <MenuItem value="REGISTRANT">Registrant</MenuItem>
                    </Select>
                  </FormControl>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Confirm Role Change</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Avatar
              src={selectedUser ? validatedImages[selectedUser.id] : null}
              sx={{ width: 48, height: 48, mr: 2 }}
            >
              <PersonIcon />
            </Avatar>
            <Typography>
              Are you sure you want to change the role of{" "}
              {selectedUser?.username || "this user"} to {newRole}?
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmRoleChange}
            color="primary"
            variant="contained"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDetailsDialog}
        onClose={() => setOpenDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar
              src={selectedUser ? validatedImages[selectedUser.id] : null}
              sx={{ width: 56, height: 56 }}
            >
              <PersonIcon />
            </Avatar>
            <Box>
              <Typography variant="h6">
                {selectedUser?.username || "N/A"}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                {[selectedUser?.firstName, selectedUser?.lastName]
                  .filter(Boolean)
                  .join(" ") || "N/A"}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Role
            </Typography>
            <Chip
              label={selectedUser?.role}
              color={getRoleColor(selectedUser?.role)}
              size="small"
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Active Sessions
          </Typography>
          <List dense>
            {selectedUser?.authSessions?.map((session, index) => (
              <ListItem key={index}>
                <ListItemIcon>
                  <SecurityIcon />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box>
                      <Typography variant="body2">
                        {session.deviceType} - {session.deviceModel}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {session.deviceOs} • {session.deviceClient}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        IP: {session.ip || "N/A"}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        Expires:{" "}
                        {new Date(session.expired_at * 1000).toLocaleString()}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Notification Settings
          </Typography>
          <List dense>
            {selectedUser?.notificationSettings?.map((setting) => (
              <ListItem key={setting.id}>
                <ListItemIcon>
                  <NotificationsIcon />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box>
                      <Typography variant="body2">
                        {setting.network} {setting.all ? "(Global)" : ""}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        mt: 0.5,
                      }}
                    >
                      {setting.confirmedTokens && (
                        <Chip label="Confirmed" size="small" />
                      )}
                      {setting.pendingTokens && (
                        <Chip label="Pending" size="small" />
                      )}
                      {setting.verifiedTokens && (
                        <Chip label="Verified" size="small" />
                      )}
                      {setting.tokenSocials && (
                        <Chip label="Socials" size="small" />
                      )}
                      {setting.keywords && (
                        <Chip label="Keywords" size="small" />
                      )}
                      {setting.transactions && (
                        <Chip label="Transactions" size="small" />
                      )}
                      {setting.approvals && (
                        <Chip label="Approvals" size="small" />
                      )}
                      {setting.stdTokens && (
                        <Chip label="Standard" size="small" />
                      )}
                      {setting.devTokens && <Chip label="Dev" size="small" />}
                      {setting.liqTokens && (
                        <Chip label="Liquidity" size="small" />
                      )}
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminUsers;
